import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Typography,
  Divider,
  message,
  Dropdown,
  Space,
  Button,
  Tooltip,
} from "antd";
import logo from "./../Assets/logo.png";
import configProduction from "./../config.production";
import { DownOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const FormComponent = () => {
  const [issueType, setIssueType] = useState("");
  const [form] = Form.useForm();
  const apiUrl = configProduction.apiUrl;
  const navigate = useNavigate();

  // State to hold form values from localStorage

  const [isSerialNumberVerified, setIsSerialNumberVerified] = useState(false);
  const [serialNumberResponse, setSerialNumberResponse] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState("Critical");
  const [additionalData, setAdditionalData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [severity, setSeverity] = useState(null);
  // Get serial number from form
  const serialNumber = form.getFieldValue("serialNo") || "";
  const [fullSerialNumberResponse, setFullSerialNumberResponse] =
    useState(null);

  // Fetch data from localStorage on component mount
  useEffect(() => {
    const customerName = localStorage.getItem("customer_name");
    const ticketSubject = localStorage.getItem("ticket_subject");
    const ticketDesc = localStorage.getItem("ticket_description");
    const contactName = localStorage.getItem("contact_name");
    const contactEmail = localStorage.getItem("contact_email");

    // Set the original values
    const originalData = {
      customername: customerName || "",
      ticketsubject: ticketSubject || "",
      ticketdesc: ticketDesc || "",
      contactname: contactName || "",
      contactemail: contactEmail || "",
    };
    setOriginalValues(originalData);

    // Set the form values
    form.setFieldsValue({
      customerName: customerName || "",
      contactName: contactName || "",
      contactEmail: contactEmail || "",
      subject: ticketSubject || "",
      description: ticketDesc || "",
    });
  }, [form]);

  // State to hold form values from localStorage
  const [originalValues, setOriginalValues] = useState({
    customername: "",
    ticketsubject: "",
    ticketdesc: "",
    contactname: "",
    contactemail: "",
  });

  // State to track edited values
  const [editedValues, setEditedValues] = useState({
    contactname: "",
    contactemail: "",
    subject: "",
    description: "",
  });

  // Handler for tracking edited values
  const handleFieldChange = (fieldName, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleDropdownChange = (value) => {
    setIssueType(value);
    // Reset serial number verification when issue type changes
    setIsSerialNumberVerified(false);
    setSerialNumberResponse("");
    setShowSubmitButton(false);
  };

  // Function to handle severity change
  const handleSeverityChange = (value) => {
    console.log("Selected Severity Level:", value);
    setSeverity(value);
  };

  const handleMenuClick = (e) => {
    const selectedItem = items.find((item) => item.key === e.key);
    if (selectedItem) {
      setSelectedSeverity(selectedItem.value);
      message.info(`Selected severity: ${selectedItem.value}`);
    }
    console.log("click", e);
  };

  const items = [
    {
      label: (
        <Tooltip title="Critical issue that requires immediate attention">
          Severity 1
        </Tooltip>
      ),
      key: "1",
      value: "Critical",
    },
    {
      label: (
        <Tooltip title="High priority issue with significant impact">
          Severity 2
        </Tooltip>
      ),
      key: "2",
      value: "High",
    },
    {
      label: (
        <Tooltip title="Moderate issue with moderate impact">
          {" "}
          Severity 3
        </Tooltip>
      ),
      key: "3",
      value: "Medium",
    },
    {
      label: (
        <Tooltip title="Low priority issue with minimal impact">
          {" "}
          Severity 4
        </Tooltip>
      ),
      key: "4",
      value: "Low",
    },
  ];

  const menuProps = {
    items: items.map((item) => ({
      ...item,
      label: React.cloneElement(item.label, {
        onClick: () => handleMenuClick({ key: item.key }),
      }),
    })),
    onClick: handleMenuClick,
  };
  const handleSerialNumberCheck = async () => {
    const serialNo = form.getFieldValue("serialNo");

    if (!serialNo) {
      message.error("Please enter a serial number");
      return;
    }

    setIsChecking(true);
    setSerialNumberResponse("");
    setIsSerialNumberVerified(false);
    setShowSubmitButton(false);
    setAdditionalData(null);
    setFullSerialNumberResponse(null); // Reset full response

    try {
      // Get org_id and ticket_id from localStorage
      const orgId = localStorage.getItem("org_id");
      const ticketId = localStorage.getItem("ticket_id");
      const accountId = localStorage.getItem("account_id");

      const requestBody = {
        account_id: accountId,
        org_id: orgId,
        ticket_id: ticketId,
      };

      const response = await fetch(
        `${apiUrl}proactive_desk_serial_check/${serialNo}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Add authentication headers if required
          },
          body: JSON.stringify(requestBody),
        }
      );

      const fullResponse = await response.json();
      setFullSerialNumberResponse(fullResponse); // Store full response

      // Check if there's a message in the response (error case)
      if (fullResponse.message) {
        throw new Error(fullResponse.message);
      }

      // Extract the nested data
      const data = fullResponse.data;

      if (data) {
        setAdditionalData({
          partNumber: data.cf_part_number || "Unknown Part Number",
          description: data.description || "Unknown",
          partCode: data.cf_service_part_code || "Not Available",
          startDate: data.cf_start_date || "Not Available",
          endDate: data.cf_end_date || "Not Available",
          customerName: data.cf_erp_master_customer_name || "Not Available",
          warrantyStatus: data.cf_warranty_status || "Not Available",
        });

        setSerialNumberResponse("Serial number verified successfully");
        setIsSerialNumberVerified(true);
        setShowSubmitButton(true);

        message.success("Serial number verified successfully");
      } else {
        throw new Error("No data found for the serial number");
      }
    } catch (error) {
      console.error("Verification Error:", error);

      const errorMessage = error.message || "Serial number verification failed";

      setSerialNumberResponse(errorMessage);
      setIsSerialNumberVerified(false);
      setShowSubmitButton(false);

      message.error(errorMessage);
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = async (values) => {
    // Check if ticket_id exists
    const ticketId = localStorage.getItem("ticket_id");
    if (!ticketId) {
      message.error("Invalid ticket. Please start the process again.");
      return;
    }

    // Only proceed if serial number is verified for Cisco H/W or S/W issues
    if (issueType === "ciscoHwSwIssue" && !isSerialNumberVerified) {
      message.error("Please verify the serial number first");
      return;
    }

    // Prepare the submission data object
    const submissionData = {
      // Directly use the data from fullSerialNumberResponse
      data:
        issueType === "ciscoHwSwIssue"
          ? fullSerialNumberResponse?.data || {}
          : {},
      ticket_details: {
        customer_name: originalValues.customername, // Non-editable field
        ticket_subject: editedValues.subject || originalValues.ticketsubject,
        ticket_description:
          editedValues.description || originalValues.ticketdesc,
        contact_name: editedValues.contactname || originalValues.contactname,
        contact_email: editedValues.contactemail || originalValues.contactemail,
        severity: selectedSeverity,
        serial_no: issueType === "ciscoHwSwIssue" ? serialNumber : "", // Serial number only for Cisco H/W or S/W issues
      },
    };

    // Disable submit button during submission
    setIsSubmitting(true);

    try {
      // API submission with the ticket_id
      const response = await fetch(
        `${apiUrl}update_customer_desk_ticket/${ticketId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(submissionData),
        }
      );
      // Check if the response is successful
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Submission failed");
      }

      // If submission is successful
      message.success("Form submitted successfully!");

      // Clear local storage after successful submission
      localStorage.removeItem("ticket_id");
      localStorage.removeItem("customer_name");
      localStorage.removeItem("ticket_subject");
      localStorage.removeItem("ticket_description");
      localStorage.removeItem("contact_name");
      localStorage.removeItem("contact_email");

      navigate("/thankyou");

      // Rest of the code remains the same...
    } catch (error) {
      console.error("Submission error:", error);
      message.error(
        error.message || "Failed to submit the form. Please try again."
      );
    } finally {
      // Re-enable submit button
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="logo" style={{ textAlign: "center", marginBottom: 20 }}>
        <img src={logo} alt="logo" className="h-12 w-32" />
      </div>
      <Typography.Title
        level={3}
        style={{ textAlign: "center", color: "#ff4d4f" }}
      >
        Proactive Service Entitlement Form
      </Typography.Title>
      <Divider />
      <Form
        form={form}
        layout="vertical"
        style={{
          maxWidth: "600px",
          margin: "auto",
          border: "1px solid #ffa940",
          padding: "20px",
        }}
      >
        {/* Customer Name (non-editable) */}
        <Form.Item
          label="Customer Name"
          name="customerName"
          rules={[{ required: true, message: "Please input customer name!" }]}
        >
          <Input readOnly />
        </Form.Item>

        {/* Contact Name */}
        <Form.Item
          label="Contact Name"
          name="contactName"
          rules={[{ required: true, message: "Please input contact name!" }]}
        >
          <Input
            onChange={(e) => handleFieldChange("contactname", e.target.value)}
          />
        </Form.Item>

        {/* Contact Email */}
        <Form.Item
          label="Contact Email"
          name="contactEmail"
          rules={[
            {
              required: true,
              type: "email",
              message: "Please input valid email!",
            },
          ]}
        >
          <Input
            onChange={(e) => handleFieldChange("contactemail", e.target.value)}
          />
        </Form.Item>

        {/* Dropdown for Issue Type */}
        <Form.Item
          label="Reason for Contact"
          name="issueType"
          rules={[{ required: true, message: "Please select a reason!" }]}
        >
          <Select
            onChange={handleDropdownChange}
            placeholder="Select Issue Type"
          >
            <Option value="ciscoHwSwIssue">Cisco H/W or S/W issue</Option>
            <Option value="nonCiscoHwSwIssue">
              Non-Cisco H/W or S/W issue
            </Option>
            <Option value="technicalQuery">Technical Query</Option>
            <Option value="salesQuery">Sales Query</Option>
            <Option value="other">Other</Option>
          </Select>
        </Form.Item>

        {/* Conditional Fields based on Issue Type */}
        {issueType === "ciscoHwSwIssue" && (
          <>
            <Form.Item
              label="Subject"
              name="subject"
              rules={[{ required: true, message: "Please input the subject!" }]}
            >
              <Input
                onChange={(e) => handleFieldChange("subject", e.target.value)}
              />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              rules={[
                { required: true, message: "Please enter a description!" },
              ]}
            >
              <Input.TextArea
                rows={4}
                onChange={(e) =>
                  handleFieldChange("description", e.target.value)
                }
              />
            </Form.Item>
            <Form.Item
              label="Select the Severity Level"
              name="severity"
              rules={[
                { required: true, message: "Please select a severity level!" },
              ]}
            >
              <Select
                onChange={handleSeverityChange}
                placeholder="Select Severity Level"
                value={severity}
              >
                <Option value="critical">
                  Priority Level - 1: Any incident that impacts Business
                  Critical applications and VIP users
                </Option>
                <Option value="high">
                  Priority Level - 2: Any incident that affects more then 50%
                  users in a location
                </Option>
                <Option value="medium">
                  Priority Level - 3: Any incident that affect the productivity
                  of the individual user but does not effect the entire group
                </Option>
                <Option value="low">
                  Priority Level - 4: Project Management & IInstallation related
                  activity
                </Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Serial No"
              name="serialNo"
              rules={[
                { required: true, message: "Please enter the serial number!" },
              ]}
              style={{ display: "flex", alignItems: "center" }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Input
                  style={{ flex: 1 }}
                  onChange={() => {
                    // Reset verification when serial number changes
                    setIsSerialNumberVerified(false);
                    setSerialNumberResponse("");
                    setShowSubmitButton(false); // keep btn hidden
                    setAdditionalData(null);
                  }}
                />
                <button
                  type="button"
                  onClick={handleSerialNumberCheck}
                  disabled={isChecking}
                  style={{
                    background: isChecking
                      ? "#cccccc"
                      : "linear-gradient(to right, #4d88ff, #40bfff)",
                    border: "none",
                    borderRadius: "5px",
                    color: "white",
                    padding: "10px 20px",
                    cursor: isChecking ? "not-allowed" : "pointer",
                    fontSize: "16px",
                  }}
                >
                  {isChecking ? "Checking..." : "Check Serial"}
                </button>
              </div>
            </Form.Item>

            {/* Display additional data after successful verification */}
            {isSerialNumberVerified && additionalData && (
              <div
                style={{
                  background: "#f0f2f5",
                  padding: "15px",
                  borderRadius: "8px",
                  marginTop: "15px",
                }}
              >
                <h3 style={{ marginBottom: "15px", color: "#1890ff" }}>
                  Details
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                  }}
                >
                  <div>
                    <strong>Part Number:</strong> {additionalData.partNumber}
                  </div>
                  <div>
                    <strong>Description:</strong> {additionalData.description}
                  </div>
                  <div>
                    <strong>Service Part Code:</strong>{" "}
                    {additionalData.partCode}
                  </div>
                  <div>
                    <strong>Start Date:</strong> {additionalData.startDate}
                  </div>
                  <div>
                    <strong>End Date:</strong> {additionalData.endDate}
                  </div>
                </div>
              </div>
            )}

            {/* Display API response */}
            {serialNumberResponse && (
              <div
                style={{
                  color: isSerialNumberVerified ? "green" : "red",
                  marginTop: "10px",
                  textAlign: "center",
                }}
              >
                {serialNumberResponse}
              </div>
            )}

            {/* Conditionally render submit button only after successful verification */}
            {showSubmitButton && (
              <Form.Item>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button
                    onClick={handleSubmit}
                    type="submit"
                    disabled={!isSerialNumberVerified || isSubmitting}
                    style={{
                      background:
                        isSerialNumberVerified && !isSubmitting
                          ? "linear-gradient(to right, #ff4d4f, #ffa940)"
                          : "#cccccc",
                      border: "none",
                      borderRadius: "5px",
                      color: "white",
                      padding: "10px 20px",
                      cursor:
                        isSerialNumberVerified && !isSubmitting
                          ? "pointer"
                          : "not-allowed",
                      fontSize: "16px",
                      width: "16rem",
                    }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </Form.Item>
            )}
          </>
        )}

        {(issueType === "nonCiscoHwSwIssue" ||
          issueType === "technicalQuery" ||
          issueType === "salesQuery" ||
          issueType === "other") && (
          <Form.Item
            label="Please enter the reason for your contacting the Proactive Service Desk"
            name="description"
            rules={[{ required: true, message: "Please provide a reason!" }]}
          >
            <Input.TextArea
              rows={4}
              onChange={(e) => handleFieldChange("description", e.target.value)}
            />
          </Form.Item>
        )}

        {issueType !== "ciscoHwSwIssue" && issueType !== "" && (
          <Form.Item>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                onClick={handleSubmit}
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: !isSubmitting
                    ? "linear-gradient(to right, #ff4d4f, #ffa940)"
                    : "#cccccc",
                  border: "none",
                  borderRadius: "5px",
                  color: "white",
                  padding: "10px 20px",
                  cursor: !isSubmitting ? "pointer" : "not-allowed",
                  fontSize: "16px",
                  width: "16rem",
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </Form.Item>
        )}
      </Form>
    </>
  );
};

export default FormComponent;
